import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { FiX, FiMessageCircle } from 'react-icons/fi'
import '../style/layout/sidebar.css'

type NavItem = {
  label: string
  icon: string
  path: string
  disabled?: boolean
}

type SidebarProps = {
  items: NavItem[]
  secondaryItems?: NavItem[]
  activePath: string
  comingSoon?: NavItem[]
  variant?: 'desktop' | 'mobile'
  className?: string
  onNavigate?: () => void
  onClose?: () => void
  onReportIssue?: () => void
}

function Sidebar({
  items,
  secondaryItems = [],
  activePath,
  comingSoon = [],
  variant = 'desktop',
  className = '',
  onNavigate,
  onClose,
  onReportIssue,
}: SidebarProps) {
  const [darkThemeOn, setDarkThemeOn] = useState(() => {
    if (typeof window === 'undefined') return false
    return (
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      window.localStorage.getItem('appDarkModeEnabled') === 'true'
    )
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const syncTheme = () => {
      setDarkThemeOn(
        document.documentElement.getAttribute('data-theme') === 'dark' ||
          window.localStorage.getItem('appDarkModeEnabled') === 'true',
      )
    }
    syncTheme()
    const observer = new MutationObserver(syncTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    window.addEventListener('storage', syncTheme)
    window.addEventListener('focus', syncTheme)
    return () => {
      observer.disconnect()
      window.removeEventListener('storage', syncTheme)
      window.removeEventListener('focus', syncTheme)
    }
  }, [])

  return (
    <aside className={`sidebar ${variant} ${className}`.trim()}>
      <div className="sidebar-top">
        <img src={darkThemeOn ? '/wordmark-dm.png' : '/wordmark.png'} alt="Scribbl" className="brand wordmark" />
        {variant === 'mobile' && (
          <button
            type="button"
            className="icon-button close-sidebar"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <FiX aria-hidden size={16} />
          </button>
        )}
      </div>
      <div className="nav-section">
        <nav className="nav">
          {items.map((item) => (
            <NavLink
              key={item.label}
              to={item.path}
              className={`nav-item ${activePath === item.path ? 'active' : ''}`}
              end={item.path === '/'}
              onClick={onNavigate}
            >
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {secondaryItems.length > 0 && <div className="nav-divider" />}

        {secondaryItems.length > 0 && (
          <nav className="nav">
            {secondaryItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.path}
                className={`nav-item ${activePath === item.path ? 'active' : ''}`}
                end={item.path === '/'}
                onClick={onNavigate}
              >
                <span className="nav-icon" aria-hidden>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        )}
      </div>
      
      {comingSoon.length > 0 && (
        <div className="coming-soon">
          <p className="coming-label">Coming soon</p>
          <div className="nav coming-nav">
            {comingSoon.map((item) => (
              <div key={item.label} className="nav-item disabled">
                <span className="nav-icon" aria-hidden>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {onReportIssue && (
        <div className="sidebar-footer">
          <button type="button" className="report-issue-btn" onClick={onReportIssue}>
            <FiMessageCircle size={14} />
            <span>Report an Issue</span>
          </button>
        </div>
      )}
    </aside>
  )
}

export default Sidebar
